<template>
  <div
    v-if="isEnabled"
    class="fixed bottom-4 right-4 z-50 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2"
  >
    <Button
      type="button"
      class="h-10 rounded-none border-0 bg-brand-fill px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-lg hover:bg-brand-fill-hover"
      size="sm"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <Bug class="size-4" />
      {{ title }}
    </Button>

    <Card
      v-if="isOpen"
      class="w-[min(28rem,calc(100vw-2rem))] rounded-none border-brand-line bg-brand-surface py-0 shadow-2xl gap-0"
    >
      <CardHeader class="grid-cols-[1fr_auto] items-center gap-3 p-4 pb-3">
        <div class="min-w-0">
          <p class="app-eyebrow">Debug</p>
          <CardTitle class="truncate text-sm font-bold">{{ title }}</CardTitle>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            class="rounded-none text-brand-muted hover:bg-brand-surface-muted hover:text-brand-ink"
            aria-label="Copy debug JSON"
            @click="copyJson"
          >
            <Clipboard class="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            class="rounded-none text-brand-muted hover:bg-brand-surface-muted hover:text-brand-ink"
            aria-label="Close debug"
            @click="isOpen = false"
          >
            <X class="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent class="space-y-3 p-4 pt-0">
        <div class="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="h-9 rounded-none border-brand-line text-[11px] font-bold uppercase tracking-[0.12em] text-brand-ink shadow-none hover:bg-brand-surface-muted"
            @click="copyAuthToken('id')"
          >
            <ClipboardCheck class="size-4" />
            {{ t("common.idToken") }}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="h-9 rounded-none border-brand-line text-[11px] font-bold uppercase tracking-[0.12em] text-brand-ink shadow-none hover:bg-brand-surface-muted"
            @click="copyAuthToken('access')"
          >
            <ClipboardCheck class="size-4" />
            {{ t("common.accessToken") }}
          </Button>
        </div>
        <pre
          class="max-h-96 overflow-auto bg-brand-surface-muted p-4 text-xs leading-5 text-brand-muted-strong"
          >{{ formattedData }}</pre
        >
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Bug, Clipboard, ClipboardCheck, X } from "lucide-vue-next";
import { toast } from "vue-sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

const props = withDefaults(
  defineProps<{
    title?: string;
    data: unknown;
  }>(),
  {
    title: "Debug",
  },
);

const auth = useAuth();
const { t } = useI18n();

const isOpen = ref(false);
const isEnabled = computed(() => isDebugEnabled(import.meta.env.VITE_PORTAL_DEBUG));
const formattedData = computed(() => stringifyDebugValue(props.data));

function copyAuthToken(type: "id" | "access") {
  const token = type === "id" ? auth.state.idToken : auth.state.accessToken;
  const label = type === "id" ? t("common.idToken") : t("common.accessToken");

  if (!token) {
    toast.warning(t("home.tokenMissing", { token: label }));
    return;
  }
  navigator.clipboard
    .writeText(token)
    .then(() => toast.success(t("home.tokenCopied", { token: label })))
    .catch(() => toast.info(t("home.tokenStored", { token: label })));
}

function stringifyDebugValue(value: unknown) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return JSON.stringify(
      {
        error: error instanceof Error ? error.message : "Debug value could not be serialized.",
      },
      null,
      2,
    );
  }
}

function isDebugEnabled(value: string | undefined) {
  if (!isLocalhost()) return false;
  if (!value) return true;
  return !["0", "false", "off", "no"].includes(value.trim().toLowerCase());
}

function isLocalhost() {
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

async function copyJson() {
  await navigator.clipboard?.writeText(formattedData.value);
}
</script>
